import React, { useState, useEffect } from 'react';
import { sendEmail } from '../utils/sendEmail';
import { useAuth } from '../context/AuthContext';
import './ListingReportModal.css';

const REASONS = [
  { value: 'fake',          label: 'Fake or fraudulent listing' },
  { value: 'wrong_info',    label: 'Incorrect information' },
  { value: 'sold',          label: 'Already sold / rented' },
  { value: 'spam',          label: 'Spam or duplicate' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'other',         label: 'Other' },
];

export default function ListingReportModal({ listing, onClose }) {
  const { user } = useAuth();
  const [reason, setReason]   = useState('');
  const [details, setDetails] = useState('');
  const [email, setEmail]     = useState(user?.email || '');
  const [status, setStatus]   = useState('idle'); // idle | sending | done | error

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) return;
    setStatus('sending');
    const result = await sendEmail({
      type: 'report-listing',
      to: 'cosmoestatescyprus@gmail.com',
      data: {
        listingId:    listing.id,
        listingTitle: listing.title,
        listingCity:  listing.city,
        reason,
        details:      details.trim() || null,
        reporterEmail: email.trim() || null,
      },
    });
    setStatus(result.ok ? 'done' : 'error');
  };

  return (
    <div className="lrm__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lrm__modal" role="dialog" aria-modal="true" aria-labelledby="lrm-title">
        <button className="lrm__close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div className="lrm__header">
          <div className="lrm__flag-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
            </svg>
          </div>
          <div>
            <h2 className="lrm__title" id="lrm-title">Report Listing</h2>
            <p className="lrm__subtitle">Help us keep CosmoEstates accurate and trustworthy.</p>
          </div>
        </div>

        {status === 'done' ? (
          <div className="lrm__success">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <h3>Report submitted</h3>
            <p>Thank you — our team will review this listing within 24 hours.</p>
            <button className="lrm__btn lrm__btn--primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form className="lrm__form" onSubmit={handleSubmit}>
            <div className="lrm__listing-pill">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>{listing.title} · {listing.city}</span>
            </div>

            <fieldset className="lrm__fieldset">
              <legend className="lrm__label">Reason for reporting <span className="lrm__required">*</span></legend>
              <div className="lrm__reasons">
                {REASONS.map(r => (
                  <label key={r.value} className={`lrm__reason ${reason === r.value ? 'lrm__reason--active' : ''}`}>
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="lrm__field">
              <label className="lrm__label" htmlFor="lrm-details">Additional details <span className="lrm__optional">(optional)</span></label>
              <textarea
                id="lrm-details"
                className="lrm__textarea"
                placeholder="Describe the issue in more detail..."
                rows={3}
                value={details}
                onChange={e => setDetails(e.target.value)}
                maxLength={500}
              />
            </div>

            <div className="lrm__field">
              <label className="lrm__label" htmlFor="lrm-email">Your email <span className="lrm__optional">(optional, for follow-up)</span></label>
              <input
                id="lrm-email"
                type="email"
                className="lrm__input"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            {status === 'error' && (
              <p className="lrm__error">Something went wrong. Please try again.</p>
            )}

            <div className="lrm__actions">
              <button type="button" className="lrm__btn lrm__btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="lrm__btn lrm__btn--primary" disabled={!reason || status === 'sending'}>
                {status === 'sending' ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
